"use client"

import * as React from "react"
import { createPortal } from "react-dom"
import { CheckIcon, ChevronDownIcon, ChevronRightIcon } from "lucide-react"

import { cn } from "@ibada/ui"

import { useRegisterMobileFormScreen } from "@/shared/components/mobile-form/mobile-form-screen-store"

/**
 * The phone form kit (the approved "grouped list" design): a full-screen form with a native top bar, grouped
 * sections of rows that open sheets, and one save pill pinned at the bottom. Labels are sentence case.
 */

type MobileFormScreenProps = {
  title: string
  cancelLabel: string
  /** The top-right action; leave it out when the bottom pill is the only one (a wizard's Next). */
  saveLabel?: string
  /** The bottom pill's label (e.g. "Enregistrer l'offrande"). */
  submitLabel: string
  /** The `<form id>` the save buttons submit. */
  formId?: string
  /** Without a `<form>` (a wizard step): the bottom pill calls this instead of submitting. */
  onSubmit?: () => void
  /** Without a `<form>`: the top-right action calls this (e.g. "Plus tard"). */
  onSave?: () => void
  onCancel: () => void
  saveDisabled?: boolean
  /** The bottom pill alone, when it gates differently from the top-right action (a wizard's Continue). */
  submitDisabled?: boolean
  saving?: boolean
  /** Under the top bar, above the content (a wizard's progress). */
  header?: React.ReactNode
  children: React.ReactNode
}

export function MobileFormScreen({
  title,
  cancelLabel,
  saveLabel,
  submitLabel,
  formId,
  onSubmit,
  onSave,
  onCancel,
  saveDisabled = false,
  submitDisabled = saveDisabled,
  saving = false,
  header,
  children,
}: MobileFormScreenProps) {
  // Inside a form the buttons submit it; outside one (a wizard) they call the handlers.
  const saveButton = formId ? { type: "submit" as const, form: formId } : { type: "button" as const, onClick: onSave }
  const submitButton = formId ? { type: "submit" as const, form: formId } : { type: "button" as const, onClick: onSubmit }
  // The app header steps aside while this is open, so the form clears the status bar itself.
  useRegisterMobileFormScreen()
  return (
    <div className='flex flex-col gap-4 pb-28' style={{ paddingTop: "var(--mobile-header-pt)" }}>
      {/* `minmax(0,auto)`: a long title truncates instead of pushing Annuler / Enregistrer off the screen. */}
      <div className='-mx-1 grid grid-cols-[1fr_minmax(0,auto)_1fr] items-center gap-2'>
        <button type='button' onClick={onCancel} className='justify-self-start rounded-full px-2 py-1.5 text-base font-bold text-primary active:bg-primary/10'>
          {cancelLabel}
        </button>
        <h1 className='truncate text-[17px] font-extrabold'>{title}</h1>
        {saveLabel ? (
          <button
            {...saveButton}
            disabled={saveDisabled || saving}
            className='justify-self-end rounded-full px-2 py-1.5 text-base font-bold text-primary active:bg-primary/10 disabled:text-muted-foreground/60'
          >
            {saveLabel}
          </button>
        ) : (
          <span />
        )}
      </div>
      {header}
      {children}
      {/* Portalled to <body>: the app's scroll container is transformed, which turns `fixed` into "scrolls with
          the page", so a pill rendered here would sit at the end of the form instead of at the bottom edge. */}
      {typeof document === "undefined" ? null : createPortal(
      <div
        className='pointer-events-none fixed inset-x-0 bottom-0 z-40 bg-gradient-to-t from-[var(--app-canvas)] from-60% to-transparent px-4 pt-6 md:hidden'
        style={{ paddingBottom: "max(1rem, calc(env(safe-area-inset-bottom) + 0.5rem))" }}
      >
        <button
          {...submitButton}
          disabled={submitDisabled || saving}
          // Disabled stays green, just faded: grey on the grey canvas read as no button at all.
          className='pointer-events-auto flex h-[3.25rem] w-full items-center justify-center rounded-full bg-primary text-base font-extrabold text-primary-foreground shadow-[0_8px_20px_-8px_rgba(5,150,105,0.6)] transition-opacity active:brightness-95 disabled:opacity-45 disabled:shadow-none'
        >
          {saving ? <span className='size-5 animate-spin rounded-full border-2 border-current border-t-transparent' /> : submitLabel}
        </button>
      </div>,
      document.body,
      )}
    </div>
  )
}

/** A titled group of rows (an iOS inset grouped list) with an optional note under it. */
export function MobileFormSection({
  title,
  footer,
  children,
  className,
}: {
  title?: string
  footer?: React.ReactNode
  children: React.ReactNode
  className?: string
}) {
  return (
    <section className={cn("flex flex-col", className)}>
      {title ? <h2 className='px-4 pb-1.5 text-sm font-bold text-muted-foreground'>{title}</h2> : null}
      <div className='divide-y divide-border/60 overflow-hidden rounded-2xl bg-card'>{children}</div>
      {footer ? <div className='px-4 pt-1.5 text-xs font-semibold text-muted-foreground'>{footer}</div> : null}
    </section>
  )
}

const ROW = "flex min-h-[3.25rem] w-full min-w-0 items-center gap-3 px-4 text-left text-base"

/** A row: label on the left, its value on the right, opening a sheet when tapped. */
export function MobileFormRow({
  label,
  value,
  placeholder,
  sublabel,
  onPress,
  invalid = false,
  disabled = false,
  trailing,
}: {
  label: string
  value?: React.ReactNode
  placeholder?: string
  /** A second line under the value (e.g. the split's breakdown). */
  sublabel?: React.ReactNode
  onPress?: () => void
  invalid?: boolean
  disabled?: boolean
  trailing?: React.ReactNode
}) {
  const hasValue = value !== undefined && value !== null && value !== ""
  const content = (
    <>
      {/* A label never takes more than about half the row (some are user data, like a budget line title), so the
          value always keeps room; both truncate rather than wrap or overflow. */}
      <span className={cn("max-w-[55%] shrink-0 truncate font-semibold", invalid ? "text-destructive" : "text-foreground")}>{label}</span>
      <span className='flex min-w-0 flex-1 flex-col items-end gap-0.5'>
        <span className={cn("max-w-full truncate font-semibold", hasValue ? "text-foreground" : "text-muted-foreground/70")}>
          {hasValue ? value : placeholder}
        </span>
        {sublabel ? <span className='max-w-full truncate text-xs font-semibold text-muted-foreground'>{sublabel}</span> : null}
      </span>
      {trailing ?? (onPress ? <ChevronRightIcon aria-hidden className='size-4 shrink-0 text-muted-foreground/50' /> : null)}
    </>
  )
  if (!onPress) return <div className={ROW}>{content}</div>
  return (
    <button type='button' onClick={onPress} disabled={disabled} className={cn(ROW, "active:bg-muted disabled:opacity-60")}>
      {content}
    </button>
  )
}

/** A row with an inline text input on the right (short values like a receipt number). */
export function MobileFormInputRow({
  label,
  invalid = false,
  ...input
}: { label: string; invalid?: boolean } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className={ROW}>
      {/* A label never takes more than about half the row (some are user data, like a budget line title), so the
          value always keeps room; both truncate rather than wrap or overflow. */}
      <span className={cn("max-w-[55%] shrink-0 truncate font-semibold", invalid ? "text-destructive" : "text-foreground")}>{label}</span>
      <input
        {...input}
        className='min-w-0 flex-1 bg-transparent text-right text-base font-semibold text-foreground outline-none placeholder:text-muted-foreground/70'
      />
    </label>
  )
}

/** A multi-line text area as its own row (a description). */
export function MobileFormTextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      rows={3}
      {...props}
      className='block min-h-[4.75rem] w-full resize-none bg-transparent px-4 py-3 text-base font-semibold text-foreground outline-none placeholder:text-muted-foreground/70'
    />
  )
}

/** "Plus d'options": less important fields, folded behind one row until asked for. */
export function MobileFormMore({
  label,
  hint,
  defaultOpen = false,
  children,
}: {
  label: string
  /** One line listing what is inside, shown while closed. Never wraps. */
  hint?: string
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = React.useState(defaultOpen)
  return (
    <div className='divide-y divide-border/60 overflow-hidden rounded-2xl bg-card'>
      <button type='button' onClick={() => setOpen((value) => !value)} aria-expanded={open} className={cn(ROW, "active:bg-muted")}>
        <span className='shrink-0 font-extrabold whitespace-nowrap text-primary'>{label}</span>
        {!open && hint ? (
          <span className='min-w-0 flex-1 truncate text-right font-semibold text-muted-foreground/70'>{hint}</span>
        ) : (
          <span className='flex-1' />
        )}
        <ChevronDownIcon aria-hidden className={cn("size-4 shrink-0 text-muted-foreground/60 transition-transform", open && "rotate-180")} />
      </button>
      {open ? children : null}
    </div>
  )
}

/**
 * A short choice (two to five options) as rows with a check on the current one, inside a `MobileFormSection`.
 * For choices inside a sheet, where a picker would stack a second sheet on top.
 */
export function MobileFormChoiceList<TValue extends string>({
  options,
  value,
  onChange,
  disabled = false,
}: {
  options: { value: TValue; label: string; description?: string }[]
  value: TValue | null | undefined
  onChange: (value: TValue) => void
  disabled?: boolean
}) {
  return (
    <>
      {options.map((option) => {
        const selected = option.value === value
        return (
          <button
            key={option.value}
            type='button'
            role='radio'
            aria-checked={selected}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={cn(ROW, "py-2 active:bg-muted disabled:opacity-60")}
          >
            <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
              <span className='truncate font-semibold text-foreground'>{option.label}</span>
              {option.description ? <span className='line-clamp-2 text-xs font-semibold text-muted-foreground'>{option.description}</span> : null}
            </span>
            {selected ? <CheckIcon aria-hidden className='size-5 shrink-0 text-primary' strokeWidth={3} /> : null}
          </button>
        )
      })}
    </>
  )
}

/** A field's error, said under its group in plain words. */
export function MobileFormError({ message }: { message?: string | null }) {
  if (!message) return null
  return <p className='px-4 pt-1.5 text-[13px] font-semibold text-destructive'>{message}</p>
}

/** A tickable row of a grouped list, for a several-choices answer shown in place (no sheet needed). */
export function MobileFormCheckRow({
  label,
  description,
  checked,
  onToggle,
  disabled = false,
}: {
  label: string
  description?: string
  checked: boolean
  onToggle: () => void
  disabled?: boolean
}) {
  return (
    <button
      type='button'
      role='checkbox'
      aria-checked={checked}
      disabled={disabled}
      onClick={onToggle}
      className='flex min-h-[3.25rem] w-full min-w-0 items-center gap-3 px-4 py-2 text-left active:bg-muted disabled:opacity-50'
    >
      <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
        <span className='truncate text-base font-semibold'>{label}</span>
        {description ? <span className='line-clamp-2 text-xs font-semibold text-muted-foreground'>{description}</span> : null}
      </span>
      {checked ? <CheckIcon aria-hidden className='size-5 shrink-0 text-primary' strokeWidth={3} /> : null}
    </button>
  )
}

/**
 * An image as one row: its preview (a logo, an avatar), the label, then Choose / Change (and Remove), picking
 * from the phone's photos. Checks the file like the desktop uploader (an image, under `maxSizeMb`) and hands back
 * only a valid one; `onInvalid` gets the reason otherwise.
 */
export function MobileFormImageRow({
  label,
  preview,
  actionLabel,
  removeLabel,
  uploading = false,
  disabled = false,
  accept = "image/*",
  maxSizeMb,
  invalidFileText,
  fileTooLargeText,
  onPick,
  onRemove,
  onInvalid,
}: {
  label: string
  /** The current image, or its stand-in (initials), at about 2.5rem. */
  preview: React.ReactNode
  /** Leave out for an image that cannot change here. */
  actionLabel?: string
  removeLabel?: string
  uploading?: boolean
  disabled?: boolean
  accept?: string
  maxSizeMb?: number
  invalidFileText?: string
  fileTooLargeText?: string
  onPick?: (file: File) => void
  onRemove?: () => void
  onInvalid?: (message: string) => void
}) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const pick = (file: File) => {
    if (!file.type.startsWith("image/")) {
      if (invalidFileText) onInvalid?.(invalidFileText)
      return
    }
    if (maxSizeMb && file.size > maxSizeMb * 1024 * 1024) {
      if (fileTooLargeText) onInvalid?.(fileTooLargeText)
      return
    }
    onPick?.(file)
  }
  return (
    <div className={cn(ROW, "py-2")}>
      <span className='flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted'>{preview}</span>
      <span className='min-w-0 flex-1 truncate font-semibold text-foreground'>{label}</span>
      {onRemove && removeLabel ? (
        <button
          type='button'
          onClick={onRemove}
          disabled={disabled || uploading}
          className='shrink-0 rounded-full px-2 py-1 font-bold text-destructive active:bg-destructive/10 disabled:opacity-60'
        >
          {removeLabel}
        </button>
      ) : null}
      {actionLabel && onPick ? (
        <>
          <button
            type='button'
            onClick={() => inputRef.current?.click()}
            disabled={disabled || uploading}
            className='shrink-0 rounded-full px-2 py-1 font-bold text-primary active:bg-primary/10 disabled:opacity-60'
          >
            {uploading ? <span className='block size-5 animate-spin rounded-full border-2 border-current border-t-transparent' /> : actionLabel}
          </button>
          <input
            ref={inputRef}
            type='file'
            accept={accept}
            className='sr-only'
            tabIndex={-1}
            onChange={(event) => {
              const file = event.target.files?.[0]
              event.target.value = ""
              if (file) pick(file)
            }}
          />
        </>
      ) : null}
    </div>
  )
}

/**
 * A choice row for INSIDE a sheet: tapping it unfolds its options under it (a check on the current one) instead of
 * opening a picker, so a sheet never stacks on a sheet. For short lists; a long or searched list belongs on a
 * full screen with `MobileFormSelectRow`.
 */
export function MobileFormInlineSelectRow({
  label,
  placeholder,
  value,
  options,
  onChange,
  clearLabel,
  disabled = false,
}: {
  label: string
  placeholder: string
  value: string | undefined
  options: { value: string; label: string }[]
  onChange: (value: string | undefined) => void
  /** A "none" choice at the top of the list (an optional field). */
  clearLabel?: string
  disabled?: boolean
}) {
  const [open, setOpen] = React.useState(false)
  const current = options.find((option) => option.value === value)?.label
  const choices = clearLabel ? [{ value: "", label: clearLabel }, ...options] : options
  return (
    <div>
      <button
        type='button'
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((next) => !next)}
        className={cn(ROW, "active:bg-muted disabled:opacity-60")}
      >
        <span className='max-w-[55%] shrink-0 truncate font-semibold text-foreground'>{label}</span>
        <span className={cn("min-w-0 flex-1 truncate text-right font-semibold", current ? "text-foreground" : "text-muted-foreground/70")}>
          {current ?? placeholder}
        </span>
        <ChevronDownIcon aria-hidden className={cn("size-4 shrink-0 text-muted-foreground/60 transition-transform", open && "rotate-180")} />
      </button>
      {open ? (
        <div className='border-t border-border/60 bg-muted/40'>
          {choices.map((choice) => {
            const selected = choice.value ? choice.value === value : !value
            return (
              <button
                key={choice.value || "__none__"}
                type='button'
                role='radio'
                aria-checked={selected}
                onClick={() => {
                  onChange(choice.value || undefined)
                  setOpen(false)
                }}
                className={cn(ROW, "min-h-11 pl-7 active:bg-muted")}
              >
                <span className='min-w-0 flex-1 truncate font-semibold text-foreground'>{choice.label}</span>
                {selected ? <CheckIcon aria-hidden className='size-5 shrink-0 text-primary' strokeWidth={3} /> : null}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
